import ServiceCategorySection from '@allesauto/core/serviceCategorySection';
import { useWhiteLabel } from '@allesauto/core/whiteLabel';
import { QueryServiceCategories, SchemaTypes } from '@allesauto/graphql';
import { Background, Routes_EN } from '@allesauto/utils/consts';
import { GetStaticPaths, GetStaticProps } from 'next';
import router from 'next/router';
import React, { FC, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import DynamicComponent from '@/components/DynamicComponent';

import { WL_INTEGRATION_SLUG_ALLESAUTO } from '@/features/whitelabel/util/getWhiteLabelIntegrationSlugs';
import { initializeApollo } from '@/global/apollo/apollo';
import SeoContainer from '@/global/containers/SeoContainer';
import Layout from '@/layouts/Layout';
import { setServiceDetails } from '@/store/redux/slices/pages/serviceBooking';

import pageData from './content.json';

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = [
    `/${WL_INTEGRATION_SLUG_ALLESAUTO}${Routes_EN.WORKSHOP_SERVICES}`,
  ];

  return {
    paths,
    fallback: 'blocking',
  };
};

type WorkshopServicesMainLayoutStaticProps = {
  bloks: any[];
  serviceCategories: SchemaTypes.WorkshopServiceCategory[];
};

export const getStaticProps: GetStaticProps<
  WorkshopServicesMainLayoutStaticProps
> = async () => {
  const apolloClient = initializeApollo();

  const { data } = await apolloClient.query<
    Pick<SchemaTypes.Query, 'workshopServiceCategories'>
  >({
    query: QueryServiceCategories,
    errorPolicy: 'all',
  });

  const serviceCategories = data?.workshopServiceCategories as
    | SchemaTypes.WorkshopServiceCategory[]
    | undefined;

  if (!serviceCategories) {
    return {
      notFound: true,
      revalidate: 60, // revalidate after 1 minute
    };
  }

  return {
    props: {
      bloks: pageData?.story?.content?.body ?? [],
      serviceCategories: serviceCategories,
    },
    revalidate: 4 * 60 * 60, // cache for 4 hours
  };
};

const WorkshopServicesMainLayout: FC<
  React.PropsWithChildren<
    React.PropsWithChildren<WorkshopServicesMainLayoutStaticProps>
  >
> = ({ bloks, serviceCategories }) => {
  const dispatch = useDispatch();
  const [showPage, setShowPage] = useState(false);

  const whiteLabel = useWhiteLabel();

  const saveService = (
    sku: string,
    description: string,
    title: string,
    abstractProductSku: string
  ) => {
    dispatch(
      setServiceDetails({ sku, description, title, abstractProductSku })
    );
  };

  useEffect(() => {
    if (whiteLabel?.merchantOrigin) {
      router.push('/error');
      return;
    }
    setShowPage(true);
  }, [whiteLabel?.merchantOrigin]);

  return showPage ? (
    <Layout>
      <SeoContainer {...pageData?.story?.meta_data} />
      <main className="bg-white">
        {bloks[0] && <DynamicComponent key={bloks[0]._uid} {...bloks[0]} />}
        {serviceCategories?.map(
          (service, i) =>
            service?.services &&
            service.services.length && (
              <ServiceCategorySection
                categoryBox={{
                  title: service?.title || '',
                  categoryName: service?.name || '',
                  offersNumber: service?.services?.length || 0,
                  currentBoxIndex: i + 1,
                }}
                tiles={service?.services}
                background={i % 2 === 0 ? Background.GREY : Background.WHITE}
                onClickHandler={saveService}
                key={service?.id}
              />
            )
        )}
        {bloks.slice(1).map((blok) => (
          <DynamicComponent key={blok._uid} {...blok} />
        ))}
      </main>
    </Layout>
  ) : null;
};

export default WorkshopServicesMainLayout;
